import { motion } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1] as const;

/** Donut chart (SVG, circumference-100 trick). Segments drawn clockwise. */
export function Donut({
  segments,
  size = 112,
  thickness = 6,
  center,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  size?: number;
  thickness?: number;
  center?: { value: string; label: string };
}) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  let offset = 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 42 42" className="h-full w-full -rotate-90">
        <circle cx="21" cy="21" r="15.915" fill="none" stroke="hsl(var(--muted))" strokeWidth={thickness} />
        {segments.map((s, i) => {
          const pct = (s.value / total) * 100;
          const el = (
            <motion.circle
              key={i}
              cx="21"
              cy="21"
              r="15.915"
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${pct} ${100 - pct}`}
              strokeDashoffset={-offset}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
            />
          );
          offset += pct;
          return el;
        })}
      </svg>
      {center && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-semibold tabular-nums leading-none">{center.value}</span>
          <span className="mt-0.5 text-[10px] text-muted-foreground">{center.label}</span>
        </div>
      )}
    </div>
  );
}

export function Legend({ items }: { items: Array<{ label: string; value: string | number; color: string }> }) {
  return (
    <div className="space-y-2 text-sm">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: it.color }} />
          <span className="truncate">{it.label}</span>
          <span className="ml-auto tabular-nums text-muted-foreground">{it.value}</span>
        </div>
      ))}
    </div>
  );
}

/** A labelled horizontal bar row (countries, concept mastery, etc.). */
export function HBar({
  label,
  pct,
  value,
  color = 'hsl(var(--primary))',
  labelWidth = 'w-20',
}: {
  label: string;
  pct: number;
  value?: string | number;
  color?: string;
  labelWidth?: string;
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className={`${labelWidth} shrink-0 truncate text-muted-foreground`}>{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        <motion.div
          className="h-full rounded-full"
          style={{ background: color }}
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
          transition={{ duration: 0.8, ease: EASE }}
        />
      </div>
      {value != null && <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground">{value}</span>}
    </div>
  );
}

/** A progress-style funnel: each step a wide bar with count + %. */
export function Funnel({ steps }: { steps: Array<{ label: string; value: number; pct: number; dim?: number }> }) {
  return (
    <div className="space-y-3 text-sm">
      {steps.map((s, i) => (
        <div key={i}>
          <div className="mb-1 flex justify-between">
            <span>{s.label}</span>
            <span className="tabular-nums text-muted-foreground">
              {s.value.toLocaleString()} · {s.pct}%
            </span>
          </div>
          <div className="h-2.5 overflow-hidden rounded bg-muted">
            <motion.div
              className="h-full rounded"
              style={{ background: `hsl(var(--primary) / ${1 - (s.dim ?? 0) * 0.18})` }}
              initial={{ width: 0 }}
              animate={{ width: `${s.pct}%` }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.08 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Attention-by-slide: a horizontal bar per slide, colour-coded by attention. */
export function AttentionBars({
  rows,
}: {
  rows: Array<{ label: string; pct: number; meta?: string }>;
}) {
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => {
        const color = r.pct >= 60 ? 'hsl(var(--primary))' : r.pct >= 35 ? 'hsl(38 92% 50%)' : 'hsl(0 72% 55%)';
        return (
          <div key={i} className="flex items-center gap-3 text-sm">
            <span className="w-28 shrink-0 truncate text-muted-foreground">{r.label}</span>
            <div className="h-6 flex-1 overflow-hidden rounded bg-muted">
              <motion.div
                className="flex h-full items-center justify-end rounded pr-2 text-[11px] font-medium text-white"
                style={{ background: color }}
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(8, Math.min(100, r.pct))}%` }}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.04 }}
              >
                {r.pct}%
              </motion.div>
            </div>
            {r.meta != null && <span className="w-12 shrink-0 text-right tabular-nums text-muted-foreground">{r.meta}</span>}
          </div>
        );
      })}
    </div>
  );
}

/** Smooth + bucket a noisy retention series into clean points (0..1 of height). */
export function smoothRetention(
  data: Array<{ second: number; reached: number }>,
  totalViews: number,
  cols = 120,
): { points: string; area: string; maxSec: number } {
  const W = 1000;
  const H = 200;
  if (!data.length || totalViews <= 0) return { points: '', area: '', maxSec: 1 };
  const maxSec = data[data.length - 1].second || 1;
  // bucket by second index into `cols` buckets, average the reached fraction
  const buckets: number[] = new Array(cols).fill(0);
  const counts: number[] = new Array(cols).fill(0);
  for (const d of data) {
    const b = Math.min(cols - 1, Math.floor(((d.second - 1) / Math.max(1, maxSec - 1)) * (cols - 1)));
    buckets[b] += Math.min(1, d.reached / totalViews);
    counts[b] += 1;
  }
  const vals = buckets.map((v, i) => (counts[i] > 0 ? v / counts[i] : null));
  // forward-fill gaps, then a light moving average
  let last = vals.find((v) => v != null) ?? 0;
  const filled = vals.map((v) => (v == null ? last : (last = v)));
  const sm = filled.map((_, i) => {
    const a = filled.slice(Math.max(0, i - 2), i + 3);
    return a.reduce((x, y) => x + y, 0) / a.length;
  });
  const x = (i: number) => (i / (cols - 1)) * W;
  const y = (v: number) => H - Math.min(1, v) * H;
  const pts = sm.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const area = `${pts} L ${W} ${H} L 0 ${H} Z`;
  return { points: pts, area, maxSec };
}
